// ─────────────────────────────────────────────────────────────────────────────
// Show-space configuration.
//
// Two origins, and ONLY two, are ever contacted by this app:
//
//   1. THIS origin (the show's static host) — the HTML/JS bundle and the
//      candidate-set JSON. Static assets only. See `showFence.ts`: the show
//      server has no per-user endpoint, so a standard asset fetch is the most
//      it can ever observe (Ticket 6 exit-test property #3).
//
//   2. The AGENT RUNTIME — the fan's OWN agent. Every personalized operation
//      (ranking, interest patches) happens here, inside the fan's Durable
//      Object, authorized by the fan's own bearer.
//
// The show is never given a third option. There is deliberately no
// `SHOW_API_BASE` in this file.
//
// Values are read from VITE_* env vars so dev/preview/prod repoint without a
// code change, falling back to the live Authority One values — the same
// pattern (and the same public constants) as the app fork's
// `src/lib/agent-runtime/config.ts` and `src/lib/supabase/env.ts`.
// ─────────────────────────────────────────────────────────────────────────────

/** The agent runtime. Mirrors AGENT_RUNTIME_SERVICE in the app fork. */
const DEFAULT_AGENT_RUNTIME_URL = 'https://authority-one-agent-runtime.e-479.workers.dev'

export const AGENT_RUNTIME_BASE_URL: string =
  import.meta.env.VITE_AGENT_RUNTIME_URL ?? DEFAULT_AGENT_RUNTIME_URL

/**
 * The PUBLIC Supabase project URL + anon (publishable) key.
 *
 * These are safe to ship in a client bundle — the anon key only grants what Row
 * Level Security allows. They are copied verbatim from the app fork's
 * `src/lib/supabase/env.ts`, which documents the same reasoning. This is the
 * *reuse* of the existing identity path required by Ticket 6 deliverable 2, not
 * a second auth system: the same project issues the same JWT that
 * `console.resolvePrincipal` already verifies (verifySupabaseJwt + the
 * app-index user→handle mapping).
 *
 * NOT A SECRET. Do not add the service-role key or the JWT signing secret here
 * — those live only in the Worker.
 */
const DEFAULT_SUPABASE_URL = 'https://naaorgpquohrhwcniatp.supabase.co'
const DEFAULT_SUPABASE_ANON_KEY = 'sb_publishable_5Dwa2Qs_sS1Ssj60hsAbNQ_h_SOC4FG'

export const SUPABASE_URL: string =
  import.meta.env.VITE_SUPABASE_URL ?? DEFAULT_SUPABASE_URL

export const SUPABASE_ANON_KEY: string =
  import.meta.env.VITE_SUPABASE_ANON_KEY ?? DEFAULT_SUPABASE_ANON_KEY

/** Owner-private agent endpoints. Both are on the app-channel allowlist. */
export const RANK_CANDIDATES_ENDPOINT = `${AGENT_RUNTIME_BASE_URL}/app/rank-candidates`
export const INTERESTS_ENDPOINT = `${AGENT_RUNTIME_BASE_URL}/app/interests`

/**
 * The show's candidate set — a STATIC, versioned asset on this origin.
 *
 * Deliberately a same-origin static file rather than an API call. A static GET
 * is the entire show-side request path for a signed-in fan, which is what makes
 * the zero-per-user-logging property structural instead of a promise.
 */
export const CANDIDATE_SET_URL =
  import.meta.env.VITE_CANDIDATE_SET_URL ?? '/candidates/btb-v2.json'

/**
 * The MEMBER SERVICE — the show's co-located group AppView
 * (authority-one-runtime/appview: com.authorityone.group.* + the Ticket 9
 * candidate-body/join methods). A third origin class with its own fence
 * (src/lib/memberDoor.ts): requests to it may carry the fan's IDENTITY (a
 * bearer, an invite code) because membership is inherently an identified
 * relationship — but NEVER preference data. Ranking/interests stay on the
 * runtime origin; the show's static origin stays bare-GET-only.
 *
 * Empty (the default) = not deployed yet: the taproom door renders with
 * honest "not open yet" copy and makes no member-service calls at all.
 */
export const MEMBER_SERVICE_URL: string = import.meta.env.VITE_MEMBER_SERVICE_URL ?? ''

/** The show's display name — used in the fixed privacy line. */
export const SHOW_NAME = 'Between Two Beers'
